import {useTheme} from '@primer/react-brand/esm'
import {usePrefersReducedMotion} from '@github-ui/use-prefers-reduced-motion'

import styles from './MonaFooterGrid.module.css'
import {useMotion} from './MonaFooterGrid.motion'
import type {MonaFooterProps} from '../types'
import {useMonaFooterLayout} from '../use-mona-footer-layout'
import {getNeighborState, GRID_LAYOUTS} from './utils/grid-layout'
import {repositoryHref} from './utils/repository-href'

export function MonaFooterGrid({
  animate = false,
  showFinalState = false,
  repos,
  onLayoutChange,
}: {
  animate?: boolean
  showFinalState?: boolean
  repos?: MonaFooterProps['repos']
  onLayoutChange?: () => void
}) {
  const {colorMode} = useTheme()
  const prefersReducedMotion = usePrefersReducedMotion()
  const layout = GRID_LAYOUTS[useMonaFooterLayout()]
  const {gridRef, activeRepositoryCells, getRepositoryInteractionProps} = useMotion({
    animate,
    showFinalState,
    prefersReducedMotion,
    layout,
    repos,
    onLayoutChange,
  })

  return (
    <div className={styles.MonaFooterGrid} data-repository-color-mode={colorMode} ref={gridRef}>
      {layout.cells.map(cell => {
        const repository = cell.repositoryIndex === null ? undefined : repos?.items[cell.repositoryIndex]
        const repositoryName = repository?.nameWithOwner.replace('/', ' / ')
        const neighbor = getNeighborState(cell, activeRepositoryCells)
        return (
          <div
            key={cell.repositoryIndex === null ? cell.id : `repository-${cell.repositoryIndex}`}
            data-cell={cell.id}
            data-last-column={cell.isLastColumn}
            data-last-row={cell.isLastRow}
            className={styles.Rect}
            style={{gridColumn: cell.colIndex + 1, gridRow: cell.rowIndex + 1}}
          >
            <div className={styles.GrayTile} style={cell.grayStyle} />
            <div className={styles.ColorTile} style={cell.colorStyle} />
            {!repository && !prefersReducedMotion && (
              <>
                <div className={styles.HoverTile} style={cell.hoverStyle} aria-hidden="true" />
                <div
                  className={styles.NeighborTile}
                  style={cell.hoverStyle}
                  data-neighbor-focused={neighbor.isActive}
                  data-neighbor-diagonal={neighbor.isDiagonal}
                  aria-hidden="true"
                />
              </>
            )}
            {repository && repos && (
              <a
                className={styles.RepositoryPanel}
                data-repository-index={cell.repositoryIndex}
                style={cell.repositoryStyle}
                href={repositoryHref(repository.url)}
                {...getRepositoryInteractionProps(cell.repositoryIndex)}
              >
                <div className={styles.RepositoryContent}>
                  <p className={styles.RepositoryName}>
                    <span className={styles.RepositoryNameText} aria-hidden="true">
                      <span>{repositoryName}</span>
                    </span>
                    <span>{repositoryName}</span>
                  </p>
                  {repository.description && <p className={styles.RepositoryDescription}>{repository.description}</p>}
                  <p className={styles.RepositoryStars}>
                    {repos.starsTemplate.replaceAll('{count}', String(repository.newStars))}
                  </p>
                </div>
              </a>
            )}
          </div>
        )
      })}
    </div>
  )
}
